import React, {
  useLayoutEffect,
  useEffect,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import IsLoading from "../components/IsLoading";
import HasError from "../components/HasError";
import NotFound from "./NotFound";
import { useSelector, useDispatch } from "react-redux";
import { fetchStationsDaily } from "../store/stationsDaily";
import { fetchStationsHourly } from "../store/stationsHourly";
import { fetchStationsTwelve } from "../store/stationTwelve";
import { fetchStationsTwentyFour } from "../store/stationTwentyFour";
import { fetchInfo } from "../store/information";
// import useJWTInterceptors from "../authentication/useJWTInterceptors";
import MapOfEachStation from "../components/MapOfEachStation";
import BarChart from "../components/BarChart";
import AqiDisplay from "../components/AqiDisplay";

const StationData = () => {
  const [selectedType, setSelectedType] = useState("daily");
  const [stationData, setStationData] = useState(null);
  const [pageIsValid, setPageIsValid] = useState(true);
  const { theRouteUrl } = useParams();
  const [temperatureHourly, setTemperatureHourly] = useState();
  const [humidityHourly, setHumidityHourly] = useState();
  const [createdDateTimeHourly, setCreatedDateTimeHourly] = useState();
  const dispatch = useDispatch();
  const [device, setDevice] = useState();
  const [deviceId, setDeviceId] = useState();
  const isLoggedIn = useSelector((state) => state.login.accessToken);
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const tokenString = process.env.REACT_APP_API_TOKEN;
  const information = useSelector((state) => state.information);
  const informationData = information.informationData;
  const informationLoading = information.informationLoading;
  const informationError = information.informationError;
  const stationsHourlyData = useSelector(
    (state) => state.stationsHourly.stationsData
  );
  const stationsDailyData = useSelector(
    (state) => state.stationsDaily.stationsData
  );
  const stationsTwelveData = useSelector(
    (state) => state.stationsTwelve.stationsData
  );
  const stationsTwentyFourData = useSelector(
    (state) => state.stationsTwentyFour.stationsData
  );

  // Fetch data every 15 minutes
  const fetchData = useCallback(() => {
    dispatch(fetchStationsDaily());
    dispatch(fetchStationsHourly());
    dispatch(fetchStationsTwelve());
    dispatch(fetchStationsTwentyFour());
    dispatch(fetchInfo());
  }, [dispatch]);

  useEffect(() => {
    fetchData();
    const intervalId = setInterval(fetchData, 900000); // 15 minutes
    return () => clearInterval(intervalId);
  }, [fetchData]);

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const getDataOfDevice = useCallback(() => {
    const deviceInfo = informationData?.find(
      (item) => item.slug === theRouteUrl
    );
    if (!deviceInfo) {
      setPageIsValid(false);
      return;
    }

    setPageIsValid(true);
    const { device, device_id } = deviceInfo;
    setDevice(device);
    setDeviceId(device_id);
    const deviceDataMap = {
      SAPA: {
        hourly: stationsHourlyData.SAPA,
        daily: stationsDailyData.SAPA,
        twelve: stationsTwelveData.SAPA,
        twentyFour: stationsTwentyFourData.SAPA,
      },
      SATA: {
        hourly: stationsHourlyData.SATA,
        daily: stationsDailyData.SATA,
        twelve: stationsTwelveData.SATA,
        twentyFour: stationsTwentyFourData.SATA,
      },
      ESPS: {
        hourly: stationsHourlyData.ESPS,
        daily: stationsDailyData.ESPS,
        twelve: stationsTwelveData.ESPS,
        twentyFour: stationsTwentyFourData.ESPS,
      },
      OSPM: {
        hourly: stationsHourlyData.OSPM,
        daily: stationsDailyData.OSPM,
        twelve: stationsTwelveData.OSPM,
        twentyFour: stationsTwentyFourData.OSPM,
      },
    };

    const hourlyData = deviceDataMap[device]?.["hourly"]?.find(
      (item) => item.device_id === device_id
    );
    if (hourlyData) {
      setTemperatureHourly(hourlyData.temperature);
      setHumidityHourly(hourlyData.humidity);
      setCreatedDateTimeHourly(hourlyData.ccreated_datetime);
    }

    const selectedData = deviceDataMap[device]?.[selectedType]?.find(
      (item) => item.device_id === device_id
    );

    setStationData(selectedData ? { ...selectedData, ...deviceInfo } : null);
  }, [
    informationData,
    selectedType,
    stationsHourlyData,
    stationsDailyData,
    stationsTwelveData,
    stationsTwentyFourData,
    theRouteUrl,
  ]);

  useEffect(() => {
    if (!informationLoading && !informationError) getDataOfDevice();
  }, [getDataOfDevice, informationLoading, informationError]);

  const navigate = useNavigate();
  const navigateAndFetchData = (theRouteUrl) => {
    const navigateUrl = "/station-data/" + theRouteUrl;
    navigate(navigateUrl);
  };

  const navigateToMultigasHistory = (theRouteUrl) => {
    const navigateUrl = "/multigas-history/" + theRouteUrl;
    navigate(navigateUrl);
  };

  const navigateToCharts = (theRouteUrl) => {
    const navigateUrl = "/station-Charts/" + theRouteUrl;
    navigate(navigateUrl);
  };

  const convertToJalali = (x) => {
    const A = x.slice(0, 10);
    const B = x.slice(11, 19);
    const C = new Date(A);
    const D = new Intl.DateTimeFormat("fa-IR").format(C);
    const E = D.concat(" ", "(", B, ")");
    return E;
  };

  const convertToJalaliDate = (x) => {
    const A = x.slice(0, 10);
    const C = new Date(A);
    const D = new Intl.DateTimeFormat("fa-IR").format(C);
    return D;
  };

  const [dailyWeather, setDailyWeather] = useState(null);

  const [weatherLoading, setWeatherLoading] = useState(true); // Added loading state
  const [weatherError, setWeatherError] = useState(null);

  const getDailyWeatherInfo = (x, y) => {
    const apiUrl = `${baseUrl}weather_daily?device=${x.toLowerCase()}&device_id=${y}`;

    axios
      .get(apiUrl, {
        headers: { Authorization: `Token ${tokenString}` },
      })
      .then((response) => {
        setDailyWeather(response.data.weather);
        setWeatherLoading(false); // Set loading to false after data is fetched
      })
      .catch((error) => {
        setWeatherError("Error fetching weather data:", error);
        setWeatherLoading(false); // Ensure loading is false on error as well
      });
  };

  useEffect(() => {
    if (device && deviceId) {
      getDailyWeatherInfo(device, deviceId);
    }
  }, [device, deviceId]);

  useEffect(() => {
    console.log(dailyWeather);
  }, [dailyWeather]);

  if (informationLoading) return <IsLoading />;
  if (informationError) return <HasError />;
  if (!pageIsValid) return <NotFound />;
  return (
    <div className="container-fluid bg-dark g-0 pt-5 border-1 border-black">
      <div className="bg-white pt-5">
        {isLoggedIn ? (
          <div className="w-100 p-0 bg-dark">
            <ul
              className="nav nav-tabs nav-justified mt-5 w-100  custom-nav-tabs custom-active-link"
              style={{ marginRight: 0, paddingRight: 0 }}
            >
              <li
                className="nav-item custom-active-link"
                title="AQI لحظه ای"
                style={{ marginRight: 0, paddingRight: 0 }}
              >
                <p
                  className="nav-link btn-link bg-dark text-white custom-nav-tabs custom-active-link"
                  style={{
                    cursor: "pointer",
                    marginRight: 0,
                    paddingRight: 0,
                    border: "none",
                    outline: "none",
                    boxShadow: "none",
                  }}
                  disabled
                >
                  اطلاعات عمومی
                </p>
              </li>

              <li
                className="nav-item bg-white"
                title="نمودارهای داده های آلودگی هوا"
              >
                <p
                  className="nav-link btn-link custom-not-active-link"
                  style={{ cursor: "pointer" }}
                  onClick={() => navigateToCharts(theRouteUrl)}
                >
                  نمودارها
                </p>
              </li>

              <li className="nav-item bg-white" title="داده های آلودگی هوا">
                <p
                  className="nav-link btn-link custom-not-active-link"
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    navigateAndFetchData(theRouteUrl);
                  }}
                >
                  تاریخچه
                </p>
              </li>

              {device === "SAPA" && [136, 137].includes(Number(deviceId)) ? (
                <li
                  className="nav-item bg-white"
                  title="تاریخچه سنسور NH3 / VOC"
                >
                  <p
                    className="nav-link btn-link custom-not-active-link"
                    style={{ cursor: "pointer" }}
                    onClick={() => navigateToMultigasHistory(theRouteUrl)}
                  >
                  سنسور NH3 / VOC
                  </p>
                </li>
              ) : null}
            </ul>
          </div>
        ) : (
          ""
        )}
        <div className="mx-auto col-12 d-flex align-items-center bg-dark pt-5">
          <div className="col-8 col-md-6 col-lg-3 pt-5">
            <select
              className="form-select mx-5 w-100 text-black border-1 border-black"
              name="aqiType"
              id="aqiType"
              onChange={(e) => setSelectedType(e.target.value)}
              style={{
                height: "50px",
                fontSize: "1rem",
              }} // Bootstrap secondary color
            >
              <option value="daily">شاخص روزانه</option>
              <option value="hourly">شاخص ساعتی</option>
              <option value="twelve">شاخص هم اکنون</option>
              <option value="twentyFour">شاخص آنلاین</option>
            </select>
          </div>
        </div>
        {stationData ? (
          <div className="bg-dark">
            <div className="p-3 bg-dark"></div>
            <div className="row flex">
              <div className=" d-flex align-items-stretch mx-auto px-5 px-lg-5 pb-lg-5 pt-lg-2 bg-dark mb-2">
                <MapOfEachStation
                  information={stationData}
                  selectedType={selectedType}
                />
              </div>
            </div>
            <div className="row g-0 bg-dark">
              <div className="col-12 col-md-5 bg-dark">
                <div className="p-3 p-lg-5">
                  <table
                    className="table table-dark table-striped table-condensed text-center"
                    style={{ width: "100%" }}
                    dir="rtl"
                  >
                    <thead>
                      <tr className="pt-3">
                        <th scope="col">نام ایستگاه</th>
                        <th scope="col" title={stationData.stationName}>
                          {stationData.stationName}
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr>
                        <th className="IRANSansXE" title="شاخص کیفیت هوا">
                          شاخص AQI
                        </th>
                        {stationData.device === "SATA" &&
                        stationData.device_id === 102 ? (
                          <td className="IRANSansXB d-flex justify-content-center ">
                            <div
                              style={{ maxWidth: 100 }}
                              title="شاخص کیفیت هوا"
                            >
                              {stationData.PM25AQI > stationData.PM10AQI ? (
                                <AqiDisplay AQI={stationData.PM25AQI} />
                              ) : (
                                <AqiDisplay AQI={stationData.PM10AQI} />
                              )}
                            </div>
                          </td>
                        ) : (
                          <td className="IRANSansXB d-flex justify-content-center ">
                            <div
                              style={{ maxWidth: 100 }}
                              title="شاخص کیفیت هوا"
                            >
                              {stationData.AQI ? (
                                <AqiDisplay AQI={stationData.AQI} />
                              ) : (
                                <AqiDisplay AQI={0} />
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                      <tr>
                        <th className="IRANSansXE">آخرین دیتای محاسبه شده</th>
                        <td className="IRANSansXB" dir="ltr">
                          {convertToJalali(stationData.ccreated_datetime)}
                        </td>
                      </tr>
                      <tr>
                        <th className="IRANSansXE" title="آلاینده عامل">
                          آلاینده عامل
                        </th>
                        {(stationData.device === "SATA" &&
                          stationData.device_id === 102) ||
                        (stationData.device === "SATA" &&
                          stationData.device_id === 131) ||
                        stationData.device === "SATA" ? (
                          <td className="IRANSansXB" title="آلاینده عامل">
                            {stationData.PM25AQI > stationData.PM10AQI
                              ? "PM2.5"
                              : "PM10"}
                          </td>
                        ) : (
                          <td className="IRANSansXB" title="آلاینده عامل">
                            {stationData.mainPollutant}
                          </td>
                        )}
                      </tr>
                      {stationData.device === "SATA" ||
                      stationData.device === "SAPA" ? (
                        <>
                          {(stationData.device === "SATA" &&
                            stationData.device_id === 102) ||
                          (stationData.device === "SAPA" &&
                            stationData.device_id === 131) ? (
                            ""
                          ) : (
                            <>
                              <tr>
                                <th
                                  className="IRANSansXE"
                                  title="غلظت گاز آلاینده کربن مونواکسید - CO"
                                >
                                  میزان آلودگی کربن مونوکسید
                                </th>
                                <td
                                  className="IRANSansXB"
                                  title="غلظت گاز آلاینده کربن مونواکسید - CO"
                                >
                                  <p>
                                    {Number(stationData.co).toFixed(
                                      stationData.device === "SATA" ||
                                        stationData.is_opm
                                        ? 1
                                        : 2
                                    )}
                                    {stationData.device === "SATA" ? (
                                      <sub>ppm</sub>
                                    ) : (
                                      <sub>ppb</sub>
                                    )}
                                  </p>
                                </td>
                              </tr>

                              <tr>
                                <th
                                  className="IRANSansXE"
                                  title="غلظت گاز آلاینده ازن  - O3"
                                >
                                  میزان آلودگی ازن
                                </th>
                                <td
                                  className="IRANSansXB"
                                  title="غلظت گاز آلاینده ازن  - O3"
                                >
                                  <p>
                                    {Number(stationData.o3).toFixed(
                                      stationData.device === "SATA"
                                        ? 0
                                        : stationData.is_opm
                                        ? 1
                                        : 2
                                    )}
                                    <sub>ppb</sub>
                                  </p>
                                </td>
                              </tr>

                              <tr>
                                <th
                                  className="IRANSansXE"
                                  title="غلظت گاز آلاینده نیتروژن دی اکسید
                      - NO2"
                                >
                                  میزان آلودگی نیتروژن دی اکسید
                                </th>
                                <td
                                  className="IRANSansXB"
                                  title="غلظت گاز آلاینده نیتروژن دی اکسید
                      - NO2"
                                >
                                  <p>
                                    {Number(stationData.o3).toFixed(
                                      stationData.device === "SATA"
                                        ? 0
                                        : stationData.is_opm
                                        ? 1
                                        : 2
                                    )}
                                    <sub>ppb</sub>
                                  </p>
                                </td>
                              </tr>

                              <tr>
                                <th
                                  className="IRANSansXE"
                                  title="غلظت گاز آلاینده  گوگرد دی اکسید
                      - SO2"
                                >
                                  میزان آلودگی گوگرد دی اکسید
                                </th>
                                <td
                                  className="IRANSansXB"
                                  title="غلظت گاز آلاینده  گوگرد دی اکسید
                      - SO2"
                                >
                                  <p>
                                    {Number(stationData.so2).toFixed(
                                      stationData.device === "SATA"
                                        ? 0
                                        : stationData.is_opm
                                        ? 1
                                        : 2
                                    )}
                                    <sub>ppb</sub>
                                  </p>
                                </td>
                              </tr>
                            </>
                          )}
                        </>
                      ) : (
                        ""
                      )}
                      <tr>
                        <th className="IRANSansXE" title="ذرات آلاینده PM 2.5">
                          ذرات معلق PM2.5
                        </th>
                        <td className="IRANSansXB" title="ذرات آلاینده PM 2.5">
                          <p>
                            {Number(stationData.pm25).toFixed(
                              stationData.is_opm ||
                                stationData.device === "SATA"
                                ? 1
                                : 2
                            )}
                            μg/m<sup>3</sup>
                          </p>
                        </td>
                      </tr>
                      <tr>
                        <th className="IRANSansXE" title="ذرات آلاینده PM 10">
                          ذرات معلق PM10
                        </th>
                        <td className="IRANSansXB" title="ذرات آلاینده PM 10">
                          <p>
                            {Number(stationData.pm10).toFixed(
                              stationData.is_opm ||
                                stationData.device === "SATA"
                                ? 1
                                : 2
                            )}
                            μg/m<sup>3</sup>
                          </p>
                        </td>
                      </tr>

                      {selectedType === "hourly" ? (
                        <tr>
                          <th
                            className="IRANSansXE"
                            title="اطلاعات هواشناسی - دما"
                          >
                            دما
                          </th>
                          <td
                            className="IRANSansXB"
                            title="اطلاعات هواشناسی - دما"
                          >
                            <p>
                              <sub>℃</sub>
                              {Number(stationData.temperature)?.toFixed(
                                stationData.device === "SATA" ? 1 : 2
                              )}
                            </p>
                          </td>
                        </tr>
                      ) : (
                        ""
                      )}

                      {/* {selectedType === "daily" ? (
                        <>
                          <tr>
                            <th
                              className="IRANSansXE"
                              title="اطلاعات هواشناسی - دما"
                            >
                              کمترین دما
                            </th>
                            <td
                              className="IRANSansXB"
                              title="اطلاعات هواشناسی - دما"
                            >
                              <p>
                                <sub>℃</sub>
                                {Number(stationData.temperature)?.toFixed(
                                  stationData.device === "SATA" ? 1 : 2
                                )}
                              </p>
                            </td>
                          </tr>
                          <tr>
                            <th
                              className="IRANSansXE"
                              title="اطلاعات هواشناسی - دما"
                            >
                              بیشترین دما
                            </th>
                            <td
                              className="IRANSansXB"
                              title="اطلاعات هواشناسی - دما"
                            >
                              <p>
                                <sub>℃</sub>
                                {Number(stationData.min_temperature)?.toFixed(
                                  stationData.device === "SATA" ? 1 : 2
                                )}
                              </p>
                            </td>
                          </tr>
                        </>
                      ) : (
                        ""
                      )} */}

                      {selectedType === "hourly" ? (
                        // || selectedType === "daily"
                        <tr>
                          <th
                            className="IRANSansXE"
                            title="اطلاعات هواشناسی - رطوبت موجود در هوا"
                          >
                            رطوبت
                          </th>
                          <td
                            className="IRANSansXB"
                            title="اطلاعات هواشناسی - رطوبت موجود در هوا"
                          >
                            <p>
                              <span>
                                {Number(stationData.humidity)?.toFixed(
                                  stationData.device === "SATA" ? 1 : 2
                                )}
                              </span>
                              <sub>%RH</sub>
                            </p>
                          </td>
                        </tr>
                      ) : (
                        ""
                      )}

                      {selectedType === "hourly" ? (
                        <>
                          {stationData.device === "SATA" && (
                            <>
                              <tr>
                                <th
                                  className="IRANSansXE"
                                  title="آلودگی ناشی از ترکیبات آلی فرار"
                                >
                                  بوی ناشی از ترکیبات آلی فرار
                                </th>
                                <td
                                  className="IRANSansXB"
                                  title="آلودگی ناشی از ترکیبات آلی فرار"
                                >
                                  <p>
                                    <span>
                                      {Number(stationData.tvoc)?.toFixed(
                                        stationData.device === "SATA" ? 0 : 2
                                      )}
                                    </span>
                                    <sub>ppm</sub>
                                  </p>
                                </td>
                              </tr>

                              <tr>
                                <th
                                  className="IRANSansXE"
                                  title="میزان آلودگی صوتی"
                                >
                                  میزان آلودگی صوت
                                </th>
                                <td
                                  className="IRANSansXB"
                                  title="میزان آلودگی صوتی"
                                >
                                  <p>
                                    <span>
                                      {Number(stationData.spl)?.toFixed(2)}
                                    </span>
                                    <sub>dB</sub>
                                  </p>
                                </td>
                              </tr>

                              <tr>
                                <th
                                  className="IRANSansXE"
                                  title="اطلاعات هواشناسی - فشار"
                                >
                                  فشار
                                </th>
                                <td
                                  className="IRANSansXB"
                                  title="اطلاعات هواشناسی - فشار"
                                >
                                  <p>
                                    <span>
                                      {Number(stationData.pressure)?.toFixed(2)}
                                    </span>
                                    <sub>kPa</sub>
                                  </p>
                                </td>
                              </tr>

                              <tr>
                                <th
                                  className="IRANSansXE"
                                  title="اطلاعات هواشناسی - جهت باد"
                                >
                                  جهت باد
                                </th>
                                <td
                                  className="IRANSansXB"
                                  title="اطلاعات هواشناسی - جهت باد"
                                >
                                  <p>
                                    {Number(stationData.winddirection)?.toFixed(
                                      0
                                    )}
                                    <sup>°</sup>
                                  </p>
                                </td>
                              </tr>

                              <tr>
                                <th
                                  className="IRANSansXE"
                                  title="اطلاعات هواشناسی - سرعت باد"
                                >
                                  سرعت باد
                                </th>
                                <td
                                  className="IRANSansXB"
                                  title="اطلاعات هواشناسی - سرعت باد"
                                >
                                  <p>
                                    <span>
                                      {Number(stationData.windspeed).toFixed(0)}
                                    </span>
                                    <sub>m/s</sub>
                                  </p>
                                </td>
                              </tr>
                            </>
                          )}
                        </>
                      ) : null}

                      {/* {selectedType === "hourly" ? (
                        <>
                          {" "}
                          {stationData.is_kit && kitData.voc ? (
                            <tr>
                              <th
                                className="IRANSansXE"
                                title="آلودگی ناشی از ترکیبات آلی فرار"
                              >
                                بوی ناشی از ترکیبات آلی فرار
                              </th>
                              <td
                                className="IRANSansXB"
                                title="آلودگی ناشی از ترکیبات آلی فرار"
                              >
                                <p>
                                  <span>{Number(kitData.voc)?.toFixed(0)}</span>
                                  <sub>ppm</sub>
                                </p>
                              </td>
                            </tr>
                          ) : (
                            ""
                          )}
                          {stationData.is_kit && kitData.spl ? (
                            <tr>
                              <th
                                className="IRANSansXE"
                                title="میزان آلودگی صوتی"
                              >
                                میزان آلودگی صوت
                              </th>
                              <td
                                className="IRANSansXB"
                                title="میزان آلودگی صوتی"
                              >
                                <p>
                                  <span>{Number(kitData.spl)?.toFixed(2)}</span>
                                  <sub>dB</sub>
                                </p>
                              </td>
                            </tr>
                          ) : (
                            ""
                          )}
                          {stationData.is_kit && kitData.pressure ? (
                            <tr>
                              <th
                                className="IRANSansXE"
                                title="اطلاعات هواشناسی - فشار"
                              >
                                فشار
                              </th>
                              <td
                                className="IRANSansXB"
                                title="اطلاعات هواشناسی - فشار"
                              >
                                <p>
                                  <span>
                                    {Number(kitData.pressure)?.toFixed(2)}
                                  </span>
                                  <sub>kPa</sub>
                                </p>
                              </td>
                            </tr>
                          ) : (
                            ""
                          )}
                        </>
                      ) : (
                        ""
                      )} */}

                      <tr>
                        <th className="IRANSansXE" colSpan="2">
                          اطلاعات روزانه سنسور های پارامتر های محیطی
                        </th>
                      </tr>
                      {dailyWeather &&
                      dailyWeather.length > 0 &&
                      dailyWeather[0].created_datetime ? (
                        <>
                          <tr>
                            <th>آخرین داده</th>
                            <td>
                              {convertToJalaliDate(
                                dailyWeather[0].created_datetime
                              )}
                            </td>
                          </tr>
                          <tr>
                            <th className="IRANSansXE">بیشترین دما</th>
                            <td className="IRANSansXB">
                              {dailyWeather[0].temperature_max ? (
                                <>
                                  {Number(
                                    dailyWeather[0].temperature_max
                                  ).toFixed(
                                    stationData.device === "SATA" ? 1 : 2
                                  )}
                                </>
                              ) : (
                                "-"
                              )}
                            </td>
                          </tr>
                          <tr>
                            <th className="IRANSansXE">کمترین دما</th>
                            <td className="IRANSansXB">
                              {dailyWeather[0].temperature_min ? (
                                <>
                                  {Number(
                                    dailyWeather[0].temperature_min
                                  ).toFixed(
                                    stationData.device === "SATA" ? 1 : 2
                                  )}
                                </>
                              ) : (
                                "-"
                              )}
                            </td>
                          </tr>
                          <tr>
                            <th className="IRANSansXE">میانگین رطوبت </th>
                            <td className="IRANSansXB">
                              {dailyWeather[0].humidity_avg ? (
                                <>
                                  {Number(dailyWeather[0].humidity_avg).toFixed(
                                    stationData.device === "SATA" ? 1 : 2
                                  )}
                                </>
                              ) : (
                                "-"
                              )}
                            </td>
                          </tr>
                        </>
                      ) : (
                        ""
                      )}

                      <tr>
                        <th className="IRANSansXE" colSpan="2">
                          اطلاعات ساعتی سنسور های پارامتر های محیطی
                        </th>
                      </tr>
                      <tr>
                        <th>آخرین داده</th>
                        <td dir="ltr">
                          {createdDateTimeHourly ? (
                            <>{convertToJalali(createdDateTimeHourly)}</>
                          ) : (
                            ""
                          )}
                        </td>
                      </tr>
                      <tr>
                        <th className="IRANSansXE">میانگین دما </th>
                        <td className="IRANSansXB">
                          <sub>℃</sub>
                          {temperatureHourly ? (
                            <>
                              {Number(temperatureHourly)?.toFixed(
                                stationData.device === "SATA" ? 1 : 2
                              )}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                      <tr>
                        <th className="IRANSansXE">میانگین رطوبت </th>
                        <td className="IRANSansXB">
                          {humidityHourly ? (
                            <>
                              {Number(humidityHourly)?.toFixed(
                                stationData.device === "SATA" ? 1 : 2
                              )}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <div></div>
                </div>
              </div>
              <div className="col-12 col-md-7 bg-dark px-2">
                {stationData.device !== "SATA" &&
                stationData.device !== "SAPA" ? (
                  <BarChart
                    data={stationData}
                    typeBasedOfTime={selectedType}
                    type="onlyParticles"
                  />
                ) : (
                  ""
                )}
                {(stationData.device === "SATA" &&
                  stationData.device_id === 102) ||
                (stationData.device === "SAPA" &&
                  stationData.device_id === 131) ? (
                  <BarChart
                    data={stationData}
                    typeBasedOfTime={selectedType}
                    type="onlyParticles"
                  />
                ) : (
                  ""
                )}
                {(stationData.device === "SATA" &&
                  stationData.device_id !== 102) ||
                (stationData.device === "SAPA" &&
                  stationData.device_id !== 131) ? (
                  <BarChart
                    data={stationData}
                    typeBasedOfTime={selectedType}
                    type="gasAndParticles"
                  />
                ) : (
                  ""
                )}
              </div>
            </div>
          </div>
        ) : selectedType === "twelve" ? (
          <div
            className=" bg-dark d-flex justify-content-center align-items-center"
            style={{ minHeight: "70vh" }}
          >
            <div className="row justify-content-center align-items-center col-md-8">
              <div className="col-md-12 text-center text-white" role="alert">
                <h2>
                  متاسفانه به علت آنلاین نبودن دستگاه، امکان محاسبه شاخص هم
                  اکنون در این لحظه وجود ندارد!
                </h2>
                <p className="py-5">
                  لطفا جهت مشاهده آخرین داده ارسال شده توسط دستگاه شاخص روزانه و
                  یا ساعتی را انتخاب فرمایید.
                </p>
              </div>
            </div>
          </div>
        ) : selectedType === "twentyFour" ? (
          <div
            className=" bg-dark d-flex justify-content-center align-items-center"
            style={{ minHeight: "70vh" }}
          >
            <div className="row justify-content-center align-items-center col-md-8">
              <div className="col-md-12 text-center text-white" role="alert">
                <h2>
                  متاسفانه به علت آنلاین نبودن دستگاه، امکان محاسبه شاخص آنلاین
                  در این لحظه وجود ندارد!
                </h2>
                <p>
                  لطفا جهت مشاهده آخرین داده ارسال شده توسط دستگاه شاخص روزانه و
                  یا ساعتی را انتخاب فرمایید.
                </p>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default StationData;
